const GLB_MAGIC = 0x46546c67;
const GLB_JSON_CHUNK = 0x4e4f534a;
const GLB_BINARY_CHUNK = 0x004e4942;

const MODEL_VISTA_PBR_TEXTURE_PROPERTIES = new Set([
    "basecolortexture",
    "basemap",
    "basecolormap",
    "maintex",
    "normaltexture",
    "bumpmap",
    "normalmap",
    "metallicroughnesstexture",
    "maskmap",
    "metallicglossmap",
    "occlusiontexture",
    "occlusionmap",
    "emissivetexture",
    "emissionmap",
    "emissivecolormap"
]);

function asUint8Array(value) {
    if (value instanceof Uint8Array) {
        return value;
    }
    if (value instanceof ArrayBuffer) {
        return new Uint8Array(value);
    }
    throw new TypeError("Expected an ArrayBuffer or Uint8Array.");
}

function parseGlb(value) {
    const bytes = asUint8Array(value);
    if (bytes.byteLength < 20) {
        throw new Error("GLB is too small to contain a valid header.");
    }

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(0, true) !== GLB_MAGIC) {
        throw new Error("Invalid GLB magic header.");
    }

    const declaredLength = view.getUint32(8, true);
    if (declaredLength > bytes.byteLength) {
        throw new Error("GLB length exceeds the available data.");
    }

    let offset = 12;
    let json = null;
    let binaryChunk = null;
    while (offset + 8 <= declaredLength) {
        const chunkLength = view.getUint32(offset, true);
        const chunkType = view.getUint32(offset + 4, true);
        const chunkStart = offset + 8;
        const chunkEnd = chunkStart + chunkLength;
        if (chunkEnd > declaredLength) {
            throw new Error("GLB chunk exceeds the declared file length.");
        }

        const chunk = bytes.subarray(chunkStart, chunkEnd);
        if (chunkType === GLB_JSON_CHUNK && json === null) {
            const text = new TextDecoder("utf-8")
                .decode(chunk)
                .replaceAll("\u0000", "")
                .trimEnd();
            json = JSON.parse(text);
        } else if (chunkType === GLB_BINARY_CHUNK && binaryChunk === null) {
            binaryChunk = chunk;
        }
        offset = chunkEnd;
    }

    if (!json) {
        throw new Error("GLB does not contain a JSON chunk.");
    }

    return { json, binaryChunk };
}

function getBufferViewBytes(modelPackage, bufferViewIndex) {
    const definition = modelPackage?.json?.bufferViews?.[bufferViewIndex];
    if (!definition || definition.buffer !== 0 || !modelPackage?.binaryChunk) {
        return null;
    }

    const start = definition.byteOffset ?? 0;
    const end = start + (definition.byteLength ?? 0);
    if (start < 0 || end > modelPackage.binaryChunk.byteLength || end < start) {
        return null;
    }
    return modelPackage.binaryChunk.subarray(start, end);
}

function decodeBase64(base64) {
    if (typeof base64 !== "string" || base64.length === 0) {
        return null;
    }
    const payload = base64.includes(",") ? base64.slice(base64.indexOf(",") + 1) : base64;
    if (typeof atob === "function") {
        const decoded = atob(payload);
        const bytes = new Uint8Array(decoded.length);
        for (let i = 0; i < decoded.length; i += 1) {
            bytes[i] = decoded.charCodeAt(i);
        }
        return bytes;
    }
    return Uint8Array.from(Buffer.from(payload, "base64"));
}

async function inflateBytes(bytes) {
    if (typeof DecompressionStream !== "undefined") {
        const tryFormat = async (format) => {
            const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream(format));
            return new Uint8Array(await new Response(stream).arrayBuffer());
        };
        try {
            return await tryFormat("deflate");
        } catch {
            return tryFormat("deflate-raw");
        }
    }
    throw new Error("This browser cannot decompress an embedded Model Vista image.");
}

async function resolveEmbeddedImage(modelPackage, extensionData, imageIndex) {
    if (!Number.isInteger(imageIndex)) {
        return null;
    }

    const records = Array.isArray(extensionData?.EmbeddedImages)
        ? extensionData.EmbeddedImages
        : [];
    const record = records.find((candidate) => candidate?.ImageIndex === imageIndex) ?? null;

    if (record?.Base64) {
        return {
            bytes: decodeBase64(record.Base64),
            mimeType: record.MimeType || "image/png",
            record
        };
    }

    const imageDefinition = modelPackage?.json?.images?.[imageIndex];
    if (Number.isInteger(imageDefinition?.bufferView)) {
        return {
            bytes: getBufferViewBytes(modelPackage, imageDefinition.bufferView),
            mimeType: imageDefinition.mimeType || record?.MimeType || "application/octet-stream",
            record
        };
    }

    if (Number.isInteger(record?.PayloadBufferViewIndex)) {
        let bytes = getBufferViewBytes(modelPackage, record.PayloadBufferViewIndex);
        const compression = String(record.PayloadCompression ?? "").toLowerCase();
        if (bytes && compression && compression !== "none") {
            bytes = await inflateBytes(bytes);
        }
        return {
            bytes,
            mimeType: record.MimeType || "application/octet-stream",
            record
        };
    }

    if (typeof imageDefinition?.uri === "string" && imageDefinition.uri.startsWith("data:")) {
        return {
            bytes: decodeBase64(imageDefinition.uri),
            mimeType: imageDefinition.uri.slice(5, imageDefinition.uri.indexOf(";")) || "application/octet-stream",
            record
        };
    }

    return null;
}

function getModelVistaCompatibility(modelPackage) {
    const json = modelPackage?.json ?? {};
    const extension = json?.extensions?.INNOWORKS_configurables ?? null;
    return {
        extension,
        schemaVersion: Number(extension?.SchemaVersion ?? (extension ? 1 : 0)),
        requiresDraco: Array.isArray(json.extensionsRequired)
            && json.extensionsRequired.includes("KHR_draco_mesh_compression"),
        sourceRenderPipeline: extension?.SourceRenderPipeline ?? "Unknown"
    };
}

function normalizeModelVistaNodeName(name) {
    return String(name ?? "").replace(/\.\d{3}$/u, "");
}

function resolveModelVistaNodePath(gltf, rootIndex, path) {
    if (!Number.isInteger(rootIndex) || !Array.isArray(gltf?.nodes)) {
        return null;
    }

    if (!path) {
        return rootIndex;
    }

    const segments = String(path).split("/").filter((segment) => segment.length > 0);
    let currentIndex = rootIndex;
    for (const segment of segments) {
        const node = gltf.nodes[currentIndex];
        if (!Array.isArray(node?.children) || node.children.length === 0) {
            return null;
        }

        let childIndex = node.children.find((child) => gltf.nodes[child]?.name === segment);
        if (childIndex === undefined) {
            childIndex = node.children.find((child) =>
                normalizeModelVistaNodeName(gltf.nodes[child]?.name) === segment
            );
        }
        if (childIndex === undefined) {
            return null;
        }
        currentIndex = childIndex;
    }

    return currentIndex;
}

function needsModelVistaMaterialReconstruction(record, standardImageCount, schemaVersion = 1) {
    if (Number(schemaVersion) >= 2) {
        return true;
    }

    const imageCount = Math.max(0, Number(standardImageCount) || 0);
    return (record?.Properties ?? []).some((property) => {
        const propertyName = String(property?.Name ?? "")
            .toLowerCase()
            .replace(/^_/, "");
        return property?.Type === "Texture"
            && MODEL_VISTA_PBR_TEXTURE_PROPERTIES.has(propertyName)
            && Number.isInteger(property?.TextureImageIndex)
            && property.TextureImageIndex >= imageCount;
    });
}

function createRuntimeAnimationOptions(clips) {
    if (!Array.isArray(clips) || clips.length === 0) {
        return [];
    }

    return [
        {
            index: 0,
            label: "None",
            animationIndex: -1,
            speed: 0,
            loop: false,
            crossFadeDuration: 0,
            transitionDuration: 0,
            thumbnail: null
        },
        ...clips.map((clip, animationIndex) => ({
            index: animationIndex + 1,
            label: typeof clip?.name === "string" && clip.name.trim().length > 0
                ? clip.name
                : `Animation ${animationIndex}`,
            animationIndex,
            speed: 1,
            loop: clip?.userData?.loop !== false,
            crossFadeDuration: 0.25,
            transitionDuration: 0,
            thumbnail: null
        }))
    ];
}

function normalizeTurntableOffsets(value) {
    const rotation = value?.rotation ?? value?.Rotation ?? value?.rotationEuler ?? {};
    const position = value?.position ?? value?.Position ?? {};
    const component = (source, lower, upper, fallback = 0) => {
        const number = Number(source?.[lower] ?? source?.[upper]);
        return Number.isFinite(number) ? number : fallback;
    };
    return {
        schemaVersion: Number(value?.version ?? value?.schemaVersion ?? value?.SchemaVersion ?? 1),
        rotationDegrees: [
            Number(value?.turntablePresentationXRotationOffsetDegrees) || component(rotation, "x", "X"),
            Number(value?.turntablePresentationYRotationOffsetDegrees) || component(rotation, "y", "Y"),
            Number(value?.turntablePresentationZRotationOffsetDegrees) || component(rotation, "z", "Z")
        ],
        position: [
            Number(value?.turntablePresentationXOffset) || component(position, "x", "X"),
            Number(value?.turntablePresentationYOffset) || component(position, "y", "Y"),
            Number(value?.turntablePresentationZOffset) || component(position, "z", "Z")
        ]
    };
}

function calculateTransformDuration(mode, speed, fromPosition, toPosition, fromRotation, toRotation) {
    const safeSpeed = Math.max(0, Number(speed) || 0);
    if (safeSpeed === 0) {
        return 0;
    }
    const normalizedMode = String(mode ?? "Time").toLowerCase();
    if (normalizedMode === "distance") {
        const a = fromPosition ?? [0, 0, 0];
        const b = toPosition ?? a;
        return Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]) / safeSpeed * 10;
    }
    if (normalizedMode === "angle") {
        const a = fromRotation ?? [0, 0, 0, 1];
        const b = toRotation ?? a;
        const dot = Math.min(1, Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]));
        const angleDegrees = (2 * Math.acos(dot) * 180) / Math.PI;
        return angleDegrees / (safeSpeed * 10);
    }
    return safeSpeed;
}

export {
    calculateTransformDuration,
    createRuntimeAnimationOptions,
    getBufferViewBytes,
    getModelVistaCompatibility,
    needsModelVistaMaterialReconstruction,
    normalizeTurntableOffsets,
    parseGlb,
    resolveModelVistaNodePath,
    resolveEmbeddedImage
};
